import { IAutoplayInfo } from 'library/ts/interfaces/IAutoplayInfo';
import { ContentType } from 'library/ts/model/enums/content/ContentType';
import { ILogger } from 'library/ts/utilities/logger/interfaces/ILogger';
import { LogService } from 'library/ts/utilities/logger/LogService';
import { IVideoApGetCurrentTime } from 'tag/ts/tag/interfaces/api/IVideoApGetCurrentTime';
import { IPlayerStateManager } from './interfaces/IPlayerStateManager';

/**
 * @copyright © Paramount Streaming
 * @author  Stan Gletski
 * @description Manages player state including time tracking and content information
 *
 * **State Management Scope:**
 * - Content type tracking for current media
 * - Autoplay capabilities and configuration
 * - Time value persistence (content, stream, playback times)
 * - Live stream UTC time management
 * - State reset and cleanup operations
 *
 * **Extracted from PlayerManager:**
 * This class consolidates all state management logic that was previously
 * scattered throughout the monolithic PlayerManager class, improving
 * maintainability and enabling focused testing of state operations.
 *
 * **Time Value Persistence:**
 * Maintains local copies of critical playback properties that persist
 * across VTG player state changes, enabling Chromecast sender functionality
 * and cross-component state synchronization.
 */
export class PlayerStateManager implements IPlayerStateManager {
	#logger: ILogger;

	// Content and playback state
	#contentType: ContentType | undefined = undefined;
	#autoplay: IAutoplayInfo;

	// Time tracking values - persist across player state changes
	#contentTime: number = NaN;
	#streamTime: number = NaN;
	#playbackTime: number = NaN;
	#liveStreamUtcTime: number = NaN;
	#liveStreamUtcDuration: number = NaN;
	#liveStreamUtcStart: number = NaN;

	constructor( tagName?: string ) {
		this.#logger = LogService.create( {
			prefixes: [ 'SmartTag:', 'Tag:', tagName || 'Unknown', 'PlayerStateManager:' ]
		} );
		this.#logger.log( 'PlayerStateManager initialized' );
	}

	/**
	 * @description Current content type being played
	 */
	get contentType(): ContentType | undefined {
		return this.#contentType;
	}

	/**
	 * @description Autoplay capabilities and configuration
	 */
	get autoplay(): IAutoplayInfo {
		return this.#autoplay;
	}

	/**
	 * @description Current time information from player
	 *
	 * **Time Value Aggregation:**
	 * Combines all tracked time values into a unified interface
	 * for consumption by other Smart Tag components.
	 *
	 * **Legacy Property Support:**
	 * Includes both legacy (content, stream) and new property names
	 * for backward compatibility with Chromecast integration.
	 */
	get currentTime(): IVideoApGetCurrentTime {
		return {
			// Legacy properties for Chromecast compatibility
			content: this.#contentTime,
			stream: this.#streamTime,
			// Current property names
			contentTime: this.#contentTime,
			streamTime: this.#streamTime,
			playbackTime: this.#playbackTime,
			liveStreamUtcTime: this.#liveStreamUtcTime,
			liveStreamUtcDuration: this.#liveStreamUtcDuration,
			liveStreamUtcStart: this.#liveStreamUtcStart
		};
	}

	/**
	 * @description Sets content type
	 * @param {ContentType | undefined} contentType
	 */
	setContentType( contentType: ContentType | undefined ): void {
		const previous = this.#contentType;
		this.#contentType = contentType;

		// Only log when state actually changes
		if ( previous !== contentType ) {
			// this.#logger.log( 'setContentType:', { previous, current: contentType } );
		}
	}

	/**
	 * @description Sets autoplay information
	 * @param {IAutoplayInfo} autoplay
	 */
	setAutoplay( autoplay: IAutoplayInfo ): void {
		this.#autoplay = autoplay;
		this.#logger.log( 'setAutoplay:', autoplay );
	}

	/**
	 * @description Updates content time value
	 * @param {number} contentTime
	 */
	setContentTime( contentTime: number ): void {
		this.#contentTime = contentTime;
		// Only log significant changes to avoid spam
		if ( contentTime % 10 < 1 ) {
			// this.#logger.log( 'setContentTime:', contentTime );
		}
	}

	/**
	 * @description Updates stream time value
	 * @param {number} streamTime
	 */
	setStreamTime( streamTime: number ): void {
		this.#streamTime = streamTime;
		// Only log significant changes to avoid spam
		if ( streamTime % 10 < 1 ) {
			// this.#logger.log( 'setStreamTime:', streamTime );
		}
	}

	/**
	 * @description Updates playback time value
	 * @param {number} playbackTime
	 */
	setPlaybackTime( playbackTime: number ): void {
		this.#playbackTime = playbackTime;
	}

	/**
	 * @description Updates live stream UTC time
	 * @param {number} liveStreamUtcTime
	 */
	setLiveStreamUtcTime( liveStreamUtcTime: number ): void {
		this.#liveStreamUtcTime = liveStreamUtcTime;
	}

	/**
	 * @description Updates live stream UTC duration
	 * @param {number} liveStreamUtcDuration
	 */
	setLiveStreamUtcDuration( liveStreamUtcDuration: number ): void {
		this.#liveStreamUtcDuration = liveStreamUtcDuration;
	}

	/**
	 * @description Updates live stream UTC start time
	 * @param {number} liveStreamUtcStart
	 */
	setLiveStreamUtcStart( liveStreamUtcStart: number ): void {
		this.#liveStreamUtcStart = liveStreamUtcStart;
	}

	/**
	 * @description Resets all time values to initial state
	 *
	 * **Time Reset Logic:**
	 * Returns all time tracking values to NaN state, which is the
	 * standard "uninitialized" state for time values in the player system.
	 */
	resetTimeValues(): void {
		this.#contentTime = NaN;
		this.#streamTime = NaN;
		this.#playbackTime = NaN;
		this.#liveStreamUtcTime = NaN;
		this.#liveStreamUtcDuration = NaN;
		this.#liveStreamUtcStart = NaN;
		this.#logger.log( 'resetTimeValues: All time values reset to NaN' );
	}

	/**
	 * @description Resets all state to initial values
	 *
	 * **Complete State Reset:**
	 * Resets all managed state including content type, autoplay info,
	 * and all time tracking values. Used during tag reset operations.
	 */
	reset(): void {
		this.#contentType = undefined;
		this.#autoplay = undefined;
		this.resetTimeValues();
		this.#logger.log( 'reset: Complete state reset performed' );
	}
}